// OpenPanel 埋点配置

import { getStoredFingerprint } from "@/lib/fingerprint/atom"
import {
	getCurrentAreaInClient,
	getCurrentLanguageInClient,
} from "@/lib/utils.client"

// 事件类型枚举
export enum TrackingEventType {
	VIEW = "view", // 页面曝光
	CLICK = "click", // 元素点击
	ACTION = "action", // 互动事件
}

export const TRACKING_CONFIG = {
	// 通用事件名称
	events: {
		PAGE_VIEW: "WebPageView",
		CLICK: "WebClick",
		PAGE_LEAVE: "WebPageLeave",
	},
} as const

// 获取 is_first_day 值
export const getIsFirstDay = (): boolean => {
	if (typeof window === "undefined") return false
	const firstVisitKey = "popmart_first_visit_timestamp"
	const firstVisitTime = localStorage.getItem(firstVisitKey)
	const currentTime = Date.now()

	if (!firstVisitTime) {
		// 第一次访问网站，记录当前时间戳
		localStorage.setItem(firstVisitKey, currentTime.toString())
		return true
	}

	// 比较当前时间与存储的时间戳是否为同一天
	const firstVisitDate = new Date(parseInt(firstVisitTime, 10)).toDateString()
	const currentDate = new Date(currentTime).toDateString()

	return firstVisitDate === currentDate
}

// 生成公共属性（用于 properties）
export const generateCommonProperties = () => {
	const userAgent = navigator.userAgent
	const isMobile =
		/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
			userAgent,
		)
	const os = /Windows/.test(userAgent)
		? "Windows"
		: /Mac/.test(userAgent)
			? "MacOS"
			: /Linux/.test(userAgent)
				? "Linux"
				: /Android/.test(userAgent)
					? "Android"
					: /iOS/.test(userAgent)
						? "iOS"
						: "unknown"

	return {
		app_version: "1.0.0",
		is_login_id: false,
		os: os,
		ip: "",
		manufacture: "unknown",
		network_type: "wifi",
		model: isMobile ? "mobile" : "desktop",
		os_version: "1.0.0",
		is_first_day: getIsFirstDay(),
		screen_height: window.screen?.height || 0,
		screen_width: window.screen?.width || 0,
	}
}

// 生成事件属性（用于 list 中的每个事件）
export const generateEventProperties = (session?: any) => {
	const _pageName = getPageNameFromUrl(window.location.pathname)
	const referrerUrl = document.referrer || ""
	const referrerUrlObj = referrerUrl ? new URL(referrerUrl) : null
	const userAgent = navigator.userAgent

	// 解析 URL 路径为 JSON 对象
	const urlPathSegments = window.location.pathname.split("/").filter(Boolean)
	const urlPathJson =
		urlPathSegments.length > 0
			? JSON.stringify(
					urlPathSegments.reduce(
						(acc, segment, index) => {
							acc[`key${index + 1}`] = segment
							return acc
						},
						{} as Record<string, string>,
					),
				)
			: "{}"

	// 解析 URL 查询参数为 JSON 对象
	const urlQueryParams = new URLSearchParams(window.location.search)
	const urlQueryEntries = Array.from(urlQueryParams.entries())
	const urlQueryJson =
		urlQueryEntries.length > 0
			? JSON.stringify(
					urlQueryEntries.reduce(
						(acc, [key, value], index) => {
							acc[`key${index + 1}`] = `${key}=${value}`
							return acc
						},
						{} as Record<string, string>,
					),
				)
			: "{}"

	// 解析来源页面路径为 JSON 对象
	const referrerPathSegments =
		referrerUrlObj?.pathname.split("/").filter(Boolean) || []
	const referrerPathJson =
		referrerPathSegments.length > 0
			? JSON.stringify(
					referrerPathSegments.reduce(
						(acc, segment, index) => {
							acc[`key${index + 1}`] = segment
							return acc
						},
						{} as Record<string, string>,
					),
				)
			: "{}"

	// 解析来源页面查询参数为 JSON 对象
	const referrerQueryParams = referrerUrlObj
		? new URLSearchParams(referrerUrlObj.search)
		: new URLSearchParams()
	const referrerQueryEntries = Array.from(referrerQueryParams.entries())
	const referrerQueryJson =
		referrerQueryEntries.length > 0
			? JSON.stringify(
					referrerQueryEntries.reduce(
						(acc, [key, value], index) => {
							acc[`key${index + 1}`] = `${key}=${value}`
							return acc
						},
						{} as Record<string, string>,
					),
				)
			: "{}"

	// 解析源页面查询参数为 JSON 对象
	const sourceQueryParams = referrerUrlObj
		? new URLSearchParams(referrerUrlObj.search)
		: new URLSearchParams()
	const sourceQueryEntries = Array.from(sourceQueryParams.entries())
	const sourceQueryJson =
		sourceQueryEntries.length > 0
			? JSON.stringify(
					sourceQueryEntries.reduce(
						(acc, [key, value], index) => {
							acc[`key${index + 1}`] = `${key}=${value}`
							return acc
						},
						{} as Record<string, string>,
					),
				)
			: "{}"

	// 根据 session 动态计算 user_type
	const user = session?.user
	const user_type = user?.isAnonymous || !user ? "guest" : "member"

	// 根据用户登录状态决定 distinct_id
	const distinct_id = user?.id || getStoredFingerprint()

	return {
		// 必需字段
		appId: 1,
		session_id: getSessionId(),
		country: getCurrentAreaInClient(),
		country_code: getCurrentAreaInClient(),
		is_login: !!user?.id,
		is_first_day: getIsFirstDay(),
		anonymous_id: getStoredFingerprint(),
		browser: userAgent.split(" ").pop() || "unknown",
		browser_engine: navigator.product || "unknown",
		browser_version: "1.0.0",
		language: getCurrentLanguageInClient(),
		url: window.location.href,
		url_host: window.location.hostname,
		url_path: window.location.pathname,
		url_path_json: urlPathJson,
		url_query: window.location.search,
		url_query_json: urlQueryJson,
		referrer_url: referrerUrl,
		referrer_url_host: referrerUrlObj?.hostname || "",
		referrer_url_path: referrerUrlObj?.pathname || "",
		referrer_url_path_json: referrerPathJson,
		referrer_url_query: referrerUrlObj?.search || "",
		referrer_url_query_json: referrerQueryJson,
		source_entry_url: referrerUrl,
		source_url: referrerUrl,
		source_url_host: referrerUrlObj?.hostname || "",
		source_url_query: referrerUrlObj?.search || "",
		source_url_query_json: sourceQueryJson,
		title: document.title,
		feature_group: "web",
		login_id: user?.id || "",
		user_need_auth: !!user?.id,
		user_id: user?.id || "",
		user_type: user_type, // 动态计算，不再是写死的 "anonymous"
		distinct_id: distinct_id,
		device_id: getStoredFingerprint(),
		area: getCurrentAreaInClient(),
		areaCode: getCurrentAreaInClient(),
		region: "",
		city: "",
		exposure_source: referrerUrl || "direct",
	}
}

// 获取页面名称
const getPageNameFromUrl = (pathname: string): string => {
	// 从路径中提取页面名称
	const segments = pathname.split("/").filter(Boolean)
	if (segments.length === 0) return "home"

	// 获取最后一个非空段作为页面名称
	const pageName = segments[segments.length - 1]
	return pageName || "home"
}

// 获取或生成 sessionId
const getSessionId = (): string => {
	if (typeof window === "undefined") {
		return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
	}

	// 从 sessionStorage 获取或生成新的 sessionId
	let sessionId = sessionStorage.getItem("track_session_id")
	if (!sessionId) {
		sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
		sessionStorage.setItem("track_session_id", sessionId)
	}
	return sessionId
}

// 简化的基础属性（仅用于 tracking-hooks 兼容）
export const getBaseProperties = () => {
	return {
		time: Date.now(),
		properties: {
			app_id: "popmart_web",
			app_name: "web_sdk",
			app_version: "1.0.0",
			is_login_id: false,
		},
	}
}

// 页面曝光事件特有属性
export const getViewEventProperties = () => {
	const baseProperties = getBaseProperties()

	return {
		...baseProperties,
		// 页面曝光事件没有额外的特有属性
	}
}

// 元素点击事件特有属性
export const getClickEventProperties = () => {
	const baseProperties = getBaseProperties()

	return {
		...baseProperties,
		element_content: "",
		element_id: "",
		element_id_md5: "",
		element_target_url: "",
	}
}

// 互动事件特有属性
export const getActionEventProperties = () => {
	const baseProperties = getBaseProperties()

	return {
		...baseProperties,
		event_duration: 0,
	}
}

// 根据事件类型获取对应的公共属性
export const getEventTypeProperties = (eventType: TrackingEventType) => {
	switch (eventType) {
		case TrackingEventType.VIEW:
			return getViewEventProperties()
		case TrackingEventType.CLICK:
			return getClickEventProperties()
		case TrackingEventType.ACTION:
			return getActionEventProperties()
		default:
			return getBaseProperties()
	}
}
